import { List, Card } from './types'
import * as config from './config'

type SortOrder =
  | 'name'
  | 'set'
  | 'released'
  | 'rarity'
  | 'color'
  | 'usd'
  | 'tix'
  | 'eur'
  | 'cmc'
  | 'power'
  | 'toughness'
  | 'edhrec'
  | 'penny'
  | 'artist'
  | 'review'

type SortDirection = 'auto' | 'asc' | 'desc'

type RollupMode = 'cards' | 'art' | 'prints'

export enum SearchError {
  MaxFetchLimit,
}

/**
 * Returns a list of Scryfall cards for a query. The query can include any
 * attributes formatted like a Scryfall search.
 *
 * Returns one 'page' of results by default. Provide the 'maxPage' option to
 * continue fetching results if present.
 */
export async function search(
  query: string,
  options: {
    maxPages?: number
    order?: SortOrder
    direction?: SortDirection
    includeExtras?: boolean
    unique?: RollupMode
    abortSignal?: AbortSignal
  } = {},
): Promise<{ cards: Card[]; error?: SearchError }> {
  const {
    maxPages = 1,
    order,
    direction,
    includeExtras,
    unique,
    abortSignal,
  } = options

  const params = new URLSearchParams({
    q: query,
  })

  if (order != null) {
    params.append('order', order)
  }

  if (direction != null) {
    params.append('dir', direction)
  }

  if (includeExtras != null) {
    params.append('include_extras', includeExtras ? 'true' : 'false')
  }

  if (unique != null) {
    params.append('unique', unique)
  }

  let url: string | undefined = `${
    config.apiURL
  }/cards/search?${params.toString()}`

  const cards: Card[] = []
  let error
  let pageIndex = 0

  while (url != null && pageIndex < maxPages) {
    const response = await fetch(url, {
      signal: abortSignal,
      headers: {
        'User-Agent': 'Lucky Paper',
      },
    })
    const json = (await response.json()) as List

    if (json.data != null) {
      cards.push(...json.data)
    }

    url = json.next_page
    pageIndex++
  }

  if (url != null && pageIndex === maxPages) {
    error = SearchError.MaxFetchLimit
  }

  return { cards, error }
}

export function searchErrorDescriptions(error?: SearchError) {
  if (error == null) {
    return undefined
  }

  const descriptions = {
    [SearchError.MaxFetchLimit]:
      'Maximum fetch limit reached. Search results are incomplete.',
  }

  return [descriptions[error]]
}
